import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { ConfirmationDialog, PageHeader, useToast } from 'components/admin';
import { Button, Input, Select, Table } from 'components/common';
import { deleteBusinessActivity, getAdminBusinessActivities } from 'apis/adminBusinessActivities';
import { getAdminBusinessActivityCategories } from 'apis/adminBusinessActivityCategories';
import { useActiveLanguagesQuery, useAdminDeleteMutation, useAdminListQuery } from 'hooks';
import '../../../styles/admin-shared.scss';

const BusinessActivitiesList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [keyword, setKeyword] = useState('');
    const [isActiveFilter, setIsActiveFilter] = useState('');
    const [languageCodeFilter, setLanguageCodeFilter] = useState('vi');
    const [deleteId, setDeleteId] = useState(null);
    const [categories, setCategories] = useState([]);
    const toast = useToast();
    const navigate = useNavigate();

    const { data: languages = [] } = useActiveLanguagesQuery();

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const res = await getAdminBusinessActivityCategories({ languageCode: languageCodeFilter, pageSize: 100 });
                if (res?.items) setCategories(res.items);
            } catch (err) {
                console.error(err);
            }
        };
        fetchCategories();
    }, [languageCodeFilter]);

    const queryParams = useMemo(() => ({
        pageIndex: currentPage,
        pageSize,
        keyword: keyword || undefined,
        isActive: isActiveFilter === '' ? '' : isActiveFilter === 'true',
        languageCode: languageCodeFilter || undefined,
    }), [currentPage, pageSize, keyword, isActiveFilter, languageCodeFilter]);
    
    const { data = { items: [], total: 0, currentPage, pageSize }, isFetching, error } = useAdminListQuery('business-activities', queryParams, getAdminBusinessActivities);
    const deleteMutation = useAdminDeleteMutation('business-activities', deleteBusinessActivity);

    useEffect(() => { setCurrentPage(1); }, [keyword, isActiveFilter, languageCodeFilter]);
    useEffect(() => { if (error) toast.error(error.message || 'Tải dữ liệu thất bại'); }, [error, toast]);

    const handleDelete = async () => {
        try {
            const res = await deleteMutation.mutateAsync(deleteId);
            if (res?.success) {
                toast.success('Xóa thành công');
            } else {
                toast.error(res?.message || 'Xóa thất bại');
            }
        } catch {
            toast.error('Xóa thất bại');
        }
        setDeleteId(null);
    };

    const getCategoryName = (categoryId) => {
        const cat = categories.find(c => c.id === categoryId);
        return cat?.translations?.[0]?.title || cat?.title || `Danh mục #${categoryId}`;
    };

    const columns = [
        { field: 'id', header: 'ID' },
        {
            header: 'Tên Giải pháp / Hoạt động',
            render: (row) => <strong>{row.translations?.[0]?.title || row.title || '—'}</strong>,
        },
        {
            header: 'Danh mục Lĩnh vực',
            render: (row) => <span className="badge badge--info">{getCategoryName(row.categoryId)}</span>,
        },
        {
            header: 'Nhãn Tag',
            render: (row) => row.badge ? <span className="status-badge active">{row.badge}</span> : '—',
        },
        { field: 'sortOrder', header: 'Thứ tự' },
        {
            header: 'Trạng thái',
            render: (row) => (
                <span className={`status-badge ${row.isActive ? 'active' : 'inactive'}`}>
                    {row.isActive ? 'Hoạt động' : 'Khóa'}
                </span>
            ),
        },
        {
            header: 'Thao tác',
            render: (row) => (
                <div className="action-buttons">
                    <button className="icon-btn icon-btn--edit" title="Sửa" onClick={() => navigate(`/admin/business-activities/edit/${row.id}`)}>
                        <FiEdit2 />
                    </button>
                    <button className="icon-btn icon-btn--delete" title="Xóa" onClick={() => setDeleteId(row.id)}>
                        <FiTrash2 />
                    </button>
                </div>
            ),
        },
    ];

    return (
        <div className="admin-page">
            <PageHeader
                title="Quản lý Hoạt động & Giải pháp Kinh doanh"
                action={<Button onClick={() => navigate('/admin/business-activities/create')}>+ Thêm Giải pháp</Button>}
            />

            <div className="filters-bar">
                <div className="filter-item">
                    <Input placeholder="Tìm kiếm giải pháp..." value={keyword} onChange={(e) => setKeyword(e.target.value)} />
                </div>
                <div className="filter-item">
                    <Select
                        value={languageCodeFilter}
                        onChange={(e) => setLanguageCodeFilter(e.target.value)}
                        options={languages.map((l) => ({ value: l.code, label: l.name }))}
                    />
                </div>
                <div className="filter-item">
                    <Select
                        value={isActiveFilter}
                        onChange={(e) => setIsActiveFilter(e.target.value)}
                        options={[
                            { value: '', label: 'Tất cả trạng thái' },
                            { value: 'true', label: 'Hoạt động' },
                            { value: 'false', label: 'Khóa' },
                        ]}
                    />
                </div>
            </div>

            <Table
                columns={columns}
                data={data.items || []}
                loading={isFetching}
                pagination={{
                    currentPage,
                    totalPages: Math.ceil((data.total || 0) / pageSize) || 1,
                    totalItems: data.total || 0,
                    onPageChange: (page) => setCurrentPage(page),
                }}
            />

            <ConfirmationDialog
                isOpen={!!deleteId}
                title="Xác nhận xóa"
                message="Bạn có chắc chắn muốn xóa giải pháp kinh doanh này?"
                onConfirm={handleDelete}
                onCancel={() => setDeleteId(null)}
            />
        </div>
    );
};

export default BusinessActivitiesList;
